'use client';

import { FC, ReactNode, useActionState, useEffect } from 'react';
import { shallow } from 'zustand/shallow';

import { RECAPTCHA_FIELD_NAME, SERVER_ACTION_NOT_SUBMITTED } from 'src/constants/constants';
import { RecaptchaAction, getRecaptchaToken } from 'src/general/helpers/getRecaptchaToken';
import { ActionResponse, ActionSuccess, ServerAction } from 'src/general/helpers/serverActions';
import { useExternalServiceStore } from 'src/stores/externalServiceStore';

type FormWithActionContent = <Schema = unknown, SuccessData extends object | string | undefined = undefined>(
  state: ActionResponse<Schema, SuccessData>,
) => ReactNode;

type Props<Schema = unknown, SuccessData extends object | string | undefined = undefined> = {
  action: ServerAction<Schema, SuccessData>;
  onSuccess: (success: ActionSuccess<SuccessData>) => void;
  onSubmit?: (data: FormData) => void;
  renderContent: FormWithActionContent;
  withRecaptcha?: RecaptchaAction;
};

export const FormWithAction: FC<Props> = ({ action, onSuccess, onSubmit, renderContent, withRecaptcha }) => {
  const [state, formAction] = useActionState(async (prevState: any, formData: FormData) => {
    onSubmit?.(formData);

    // If recaptcha is enabled, add the token to the payload.
    if (withRecaptcha) {
      const token = await getRecaptchaToken(withRecaptcha);
      formData.append(RECAPTCHA_FIELD_NAME, token);
    }

    return action(prevState, formData);
  }, SERVER_ACTION_NOT_SUBMITTED);

  const { setShouldLoadRecaptcha } = useExternalServiceStore(
    ({ setShouldLoadRecaptcha }) => ({ setShouldLoadRecaptcha }),
    shallow,
  );

  useEffect(() => {
    if (state?.status === 'success') {
      onSuccess(state);
    }
  }, [state]);

  useEffect(() => {
    if (withRecaptcha) {
      setShouldLoadRecaptcha();
    }
  }, [withRecaptcha]);

  return (
    <form action={formAction} noValidate>
      {renderContent(state)}
    </form>
  );
};
